import { useCallback, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import AgGrid from "../../../UI/AgGrid";
import {
  ColDef,
  GridReadyEvent,
  RowDoubleClickedEvent,
  IServerSideGetRowsParams,
} from "ag-grid-community";
import { useNavigate } from "react-router-dom";
import { dateFilterModel } from "../../../../utils/Formatter/AgGridFilter";
import { GraphQLClient } from "graphql-request";
import { createGraphQLClientWithMiddleware } from "../../../../services/graphqlClient";
import { useCustomerDepositColumnDefs } from "./columnDefs";
import { CUSTOMER_DEPOSIT_AGGRID } from "../../../../services/AgGrid/SalesAgGrid";

interface Props {
  isFilter?: string | null;
  referenceUniqueId?: string;
}

const CustomerDepositTable = ({ isFilter, referenceUniqueId }: Props) => {
  const gridRef = useRef<AgGridReact<any>>(null);
  const navigate = useNavigate();

  const branch_id = parseInt(sessionStorage.getItem("branch-id") || "");

  const columnDef: ColDef[] | undefined = useCustomerDepositColumnDefs();

  const graphQLClientWithHeaderSales: GraphQLClient =
    createGraphQLClientWithMiddleware("sales");

  const datasource = {
    async getRows(params: IServerSideGetRowsParams) {
      const { request } = params;
      const { startRow, endRow, filterModel, sortModel } = request;
      const {
        created_date,
        issue_date,
        due_date,
        created_by_object,
        tag_list,
        related_user_object_list,
        ...otherFilterModel
      } = filterModel;
      const formatFilter = {
        ...otherFilterModel,
        created_date: dateFilterModel(created_date),
        issue_date: dateFilterModel(issue_date),
        due_date: dateFilterModel(due_date),
        created_by: created_by_object,
        tag_list: tag_list
          ? {
              filterType: "array",
              type: "hasSome",
              values: tag_list.values,
            }
          : undefined,
        related_user_list: related_user_object_list
          ? {
              filterType: "array",
              type: "hasSome",
              values: related_user_object_list.values,
            }
          : undefined,
        reference_unique_id: referenceUniqueId
          ? {
              filterType: "text",
              type: "equals",
              filter: referenceUniqueId,
            }
          : undefined,
        branch_id:
          branch_id !== -1
            ? {
                filterType: "number",
                type: "equals",
                filter: branch_id,
              }
            : undefined,
      };
      try {
        const { CustomerDepositViewsAggrid } =
          await graphQLClientWithHeaderSales.request(CUSTOMER_DEPOSIT_AGGRID, {
            aggridInput: {
              startRow,
              endRow,
              filterModel: formatFilter,
              sortModel,
            },
          });
        params.success({
          rowData: CustomerDepositViewsAggrid.results as any[],
          rowCount: CustomerDepositViewsAggrid.count as number,
        });
      } catch (err) {
        params.fail();
      }
    },
  };

  const onFilterChanged = useCallback(
    (params: any) => {
      const instance = params.api.getFilterInstance("status");
      switch (isFilter) {
        case "draft":
          instance?.setModel({ values: ["draft"] });
          break;
        case "wait_deduction":
          instance?.setModel({ values: ["wait_deduction"] });
          break;
        case "partially_deduction":
          instance?.setModel({ values: ["partially_deduction"] });
          break;
        case "finished":
          instance?.setModel({ values: ["finished"] });
          break;
        case "cancelled":
          instance?.setModel({ values: ["cancelled"] });
          break;
        default:
          instance?.setModel({});
          break;
      }
      params.api.onFilterChanged();
    },
    [isFilter]
  );

  const onGridReady = (params: GridReadyEvent) => {
    onFilterChanged(params);
    params.api.setServerSideDatasource(datasource);
  };

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      onFilterChanged(gridRef.current);
    }
  }, [gridRef, onFilterChanged]);

  const onRowDoubleClicked = (params: RowDoubleClickedEvent) => {
    navigate(`/sales/customer-deposit/${encodeURIComponent(params.data.id)}`);
  };

  return (
    <AgGrid
      ref={gridRef}
      columnDefs={columnDef}
      height={665}
      onRowDoubleClicked={onRowDoubleClicked}
      onGridReady={onGridReady}
      path={"/sales/customer-deposit"}
    />
  );
};

export default CustomerDepositTable;
